import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { insightsLens } from '@upright/ui/common/upright/core/impact-lenses/insights';
import { netImpactLens } from '@upright/ui/common/upright/core/impact-lenses/net-impact';
import { paisLens } from '@upright/ui/common/upright/core/impact-lenses/pais';
import { sdgLens } from '@upright/ui/common/upright/core/impact-lenses/sdg';
import { targetsLens } from '@upright/ui/common/upright/core/impact-lenses/targets';
import { taxonomyLens } from '@upright/ui/common/upright/core/impact-lenses/taxonomy';
import { materialityLens } from '@upright/ui/common/upright/core/impact-lenses/materiality';
import { financialEffectsLens } from '@upright/ui/common/upright/core/impact-lenses/financial-effects';
import { climateRiskLens } from '@upright/ui/common/upright/core/impact-lenses/climate-risk';
import { ImpactLens } from '@upright/ui/common/upright/core/impact-lenses/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import {
  CompanyDataSetId,
  CompanyGroupDataSetId,
  DataSetId,
  PortfolioDataSetId,
} from '@upright/common/iam/access/access-details/types';

export function getLens<T extends ImpactLensType>(type: T): ImpactLens<any, any, any> {
  switch (type) {
    case ImpactLensType.EU_TAXONOMY:
      return taxonomyLens;
    case ImpactLensType.INSIGHTS:
      return insightsLens;
    case ImpactLensType.UPRIGHT_NET_IMPACT:
      return netImpactLens;
    case ImpactLensType.TARGETS:
      return targetsLens;
    case ImpactLensType.EU_PAIS:
      return paisLens;
    case ImpactLensType.SDGS:
      return sdgLens;
    case ImpactLensType.CSRD:
      return materialityLens;
    case ImpactLensType.FINANCIAL_EFFECTS:
      return financialEffectsLens;
    case ImpactLensType.CLIMATE_RISK:
      return climateRiskLens;
  }
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  throw Error(`Unknown lens: ${type}`);
}

export type EntityTypesWithLensViews = ModelEntityType.COMPANY | ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;

export const companyViewLenses = [
  ImpactLensType.UPRIGHT_NET_IMPACT,
  ImpactLensType.CSRD,
  ImpactLensType.FINANCIAL_EFFECTS,
  ImpactLensType.CLIMATE_RISK,
  ImpactLensType.SDGS,
  ImpactLensType.EU_TAXONOMY,
  ImpactLensType.EU_PAIS,
  // Note: INSIGHTS and TARGETS are hidden from the navigation but the code is kept for potential future use
  // ImpactLensType.INSIGHTS,
  // ImpactLensType.TARGETS,
] as const;

export const companyGroupViewLenses = [
  ImpactLensType.UPRIGHT_NET_IMPACT,
  ImpactLensType.CSRD,
  ImpactLensType.SDGS,
  ImpactLensType.EU_TAXONOMY,
  ImpactLensType.EU_PAIS,
] as const;

export const portfolioViewLenses = [
  ImpactLensType.UPRIGHT_NET_IMPACT,
  ImpactLensType.SDGS,
  ImpactLensType.EU_TAXONOMY,
  ImpactLensType.EU_PAIS,
] as const;

/*
 * This map is used to determine which lenses are available for a given entity type.
 * Lenses are only used with company, group and portfolio entities.
 */
export const entityViewsToAvailableLensesMap: Record<EntityTypesWithLensViews, readonly ImpactLensType[]> = {
  [ModelEntityType.COMPANY]: companyViewLenses,
  [ModelEntityType.GROUP]: companyGroupViewLenses,
  [ModelEntityType.PORTFOLIO]: portfolioViewLenses,
} as const;

export type CompanyViewLens = (typeof companyViewLenses)[number];
export type CompanyGroupViewLens = (typeof companyGroupViewLenses)[number];
export type PortfolioViewLens = (typeof portfolioViewLenses)[number];

const companyViewLensToDataSetIdMap: Record<CompanyViewLens, CompanyDataSetId> = {
  [ImpactLensType.UPRIGHT_NET_IMPACT]: DataSetId.netImpact,
  [ImpactLensType.EU_TAXONOMY]: DataSetId.euTaxonomy,
  [ImpactLensType.SDGS]: DataSetId.sdgAlignment,
  [ImpactLensType.EU_PAIS]: DataSetId.euSfdr,
  // Note: INSIGHTS and TARGETS mappings removed as these lenses are hidden from navigation
  // [ImpactLensType.INSIGHTS]: DataSetId.insights,
  // [ImpactLensType.TARGETS]: DataSetId.targets,
  [ImpactLensType.CSRD]: DataSetId.csrd,
  [ImpactLensType.FINANCIAL_EFFECTS]: DataSetId.financialEffects,
  [ImpactLensType.CLIMATE_RISK]: DataSetId.climateRisk,
};

const companyGroupViewLensToDataSetIdMap: Record<CompanyGroupViewLens, CompanyGroupDataSetId> = {
  [ImpactLensType.UPRIGHT_NET_IMPACT]: DataSetId.netImpact,
  [ImpactLensType.EU_TAXONOMY]: DataSetId.euTaxonomy,
  [ImpactLensType.SDGS]: DataSetId.sdgAlignment,
  [ImpactLensType.EU_PAIS]: DataSetId.euSfdr,
  [ImpactLensType.CSRD]: DataSetId.csrd,
};

const portfolioViewLensToDataSetIdMap: Record<PortfolioViewLens, PortfolioDataSetId> = {
  [ImpactLensType.UPRIGHT_NET_IMPACT]: DataSetId.netImpact,
  [ImpactLensType.EU_TAXONOMY]: DataSetId.euTaxonomy,
  [ImpactLensType.SDGS]: DataSetId.sdgAlignment,
  [ImpactLensType.EU_PAIS]: DataSetId.euSfdr,
};

const allLensToDataSetIdMaps = {
  ...companyViewLensToDataSetIdMap,
  ...companyGroupViewLensToDataSetIdMap,
  ...portfolioViewLensToDataSetIdMap,
};

export function getLensByDataSetId<T extends ImpactLensType, D>(dataSetId: D): T | undefined {
  const entries = Object.entries(allLensToDataSetIdMaps) as Array<[T, D]>;
  const match = entries.find(([_, id]) => id === dataSetId);
  return match ? match[0] : undefined;
}
